import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import CloseOutlined from '@ant-design/icons/CloseOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';

/**
 * Global Confirmation Popup Component
 * Design matched directly from reference images:
 * - Clean white card with rounded corners (12px)
 * - Header: Bold title ("Manual Attendance" style), X close icon on top right, light divider border below header
 * - Body: Clean spacing with prompt message
 * - Footer: Border divider above footer
 * - Cancel button: Outlined button with rounded corners (8px)
 * - Action/Confirm Button:
 *     - Default State (Image 1): Outlined/Solid styled button with left icon
 *     - Hover State (Image 2): Dark slate navy background (#344054 / #334155), crisp white text (#ffffff),
 *       white icon (#ffffff), rounded corners (8px), subtle shadow (0 4px 14px rgba(52, 64, 84, 0.35)),
 *       and translateY(-1px) smooth transition.
 */
export default function ConfirmationPopup({
  open = false,
  onClose,
  onConfirm,
  title = 'Confirmation',
  message = 'Are you sure you want to proceed with this action?',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  loading = false,
  icon,
  maxWidth = 'xs'
}) {
  if (!open) return null;

  const theme = useTheme();
  const dark = localStorage.getItem('hrmsThemeMode') === 'dark';
  const bg = dark ? '#1E293B' : '#ffffff';
  const border = dark ? '#334155' : '#eef2f6';
  const titleColor = dark ? '#F1F5F9' : '#1e293b';
  const bodyColor = dark ? '#94A3B8' : '#475569';
  const cancelBorder = dark ? '#475569' : '#cbd5e1';
  const cancelColor = dark ? '#94A3B8' : '#475569';

  const defaultIcon = icon || <SaveOutlined style={{ fontSize: 15 }} />;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth
      sx={{
        zIndex: 99999
      }}
      PaperProps={{
        sx: {
          width: maxWidth === 'xs' ? 440 : maxWidth === 'sm' ? 520 : 600,
          maxWidth: '94vw',
          borderRadius: '12px',
          border: `1px solid ${border}`,
          boxShadow: '0 20px 48px -8px rgba(15, 23, 42, 0.18)',
          bgcolor: bg,
          overflow: 'hidden',
          m: 'auto'
        }
      }}
    >
      {/* ── Header Design (Matching Reference Image 1) ── */}
      <DialogTitle
        sx={{
          py: 0.5,
          px: 3,
          boxSizing: 'border-box',
          bgcolor: bg,
          borderBottom: `1px solid ${border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '1.05rem',
            color: titleColor,
            lineHeight: 1.2
          }}
        >
          {title}
        </Typography>

        <IconButton
          size="small"
          onClick={onClose}
          disabled={loading}
          sx={{
            width: 32,
            height: 32,
            color: '#64748b',
            '&:hover': {
              bgcolor: '#f1f5f9',
              color: '#0f172a'
            }
          }}
        >
          <CloseOutlined style={{ fontSize: 14 }} />
        </IconButton>
      </DialogTitle>

      {/* ── Body Content ── */}
      <DialogContent
        sx={{
          p: 3,
          bgcolor: bg
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: bodyColor,
            fontSize: '0.9rem',
            fontWeight: 500,
            lineHeight: 1.6
          }}
        >
          {message}
        </Typography>
      </DialogContent>

      {/* ── Footer Design & Button Hover (Matching Reference Image 1 & Image 2) ── */}
      <DialogActions
        sx={{
          py: 0.5,
          px: 3,
          boxSizing: 'border-box',
          bgcolor: bg,
          borderTop: `1px solid ${border}`,
          justifyContent: 'flex-end',
          gap: 0.5
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={onClose}
          disabled={loading}
          sx={{
            px: 2.5,
            height: 32,
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '0.85rem',
            textTransform: 'none',
            borderColor: cancelBorder,
            color: cancelColor,
            '&:hover': {
              borderColor: '#94a3b8',
              bgcolor: dark ? '#273444' : '#f8fafc',
              color: dark ? '#F1F5F9' : '#1e293b'
            }
          }}
        >
          {cancelLabel}
        </Button>

        <Button
          variant="outlined"
          size="small"
          onClick={onConfirm}
          disabled={loading}
          // startIcon={defaultIcon}
          sx={{
            px: 2.5,
            height: 32,
            borderRadius: '8px',
            fontWeight: 700,
            fontSize: '0.85rem',
            textTransform: 'none',
            color: 'var(--primary-main, #334155)',
            bgcolor: '#ffffff',
            border: '1px solid var(--primary-main, #334155)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              bgcolor: 'var(--primary-main, #475569)',
              color: '#ffffff',
              borderColor: 'var(--primary-main, #475569)',
              boxShadow: '0 4px 14px rgba(0, 0, 0, 0.18)',
              transform: 'translateY(-1px)',
              '& .MuiButton-startIcon': {
                color: '#ffffff'
              }
            },
            '& .MuiButton-startIcon': {
              transition: 'color 0.2s ease-in-out'
            }
          }}
        >
          {loading ? 'Processing...' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
